import { useState } from 'react';

interface UsePaginationProps {
  totalItems: number;
  initialPage?: number;
  initialRowsPerPage?: number;
}

interface UsePaginationReturn {
  page: number;
  rowsPerPage: number;
  totalPages: number;
  startItem: number;
  endItem: number;
  paginatedData: <T>(data: T[]) => T[];
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: any) => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToPage: (pageNumber: number) => void;
  resetPage: () => void;
}

export const usePagination = ({
  totalItems,
  initialPage = 0,
  initialRowsPerPage = 10,
}: UsePaginationProps): UsePaginationReturn => {
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalItems);

  const paginatedData = <T>(data: T[]): T[] => {
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const goToFirstPage = () => {
    setPage(0);
  };

  const goToLastPage = () => {
    setPage(totalPages - 1);
  };

  const goToNextPage = () => {
    setPage(prev => Math.min(prev + 1, totalPages - 1));
  };

  const goToPreviousPage = () => {
    setPage(prev => Math.max(prev - 1, 0));
  };

  const goToPage = (pageNumber: number) => {
    setPage(Math.max(0, Math.min(pageNumber, totalPages - 1)));
  };

  const resetPage = () => {
    setPage(0);
  };

  return {
    page,
    rowsPerPage,
    totalPages,
    startItem,
    endItem,
    paginatedData,
    handleChangePage,
    handleChangeRowsPerPage,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    resetPage,
  };
};
